import { store } from '../../index';

import {
    IPortalAssetsInitialize,
    IPortalAssetsSetLanguageCode,
    PortalAssetsActions,
} from './Actions';

import {
    PortalTextBorrowerAnonymousSubmission,
    PortalTextLogin,
    PortalTextSectionIds,
    PortalTextTimeoutMessaging
} from '../../Models/Api/strongbox.financialportal';

import {
    GetTenantAccountingSystemsTextContent,
    GetTenantAnonymousTextContent,
    GetTenantTextContent,
} from '../../Services/Tenant';
import { AppSettingsActions } from '../AppSettings';

import { LogException } from '../../Utils/Logging';

function PortalAssetsInitialize(supportMultipleLanguages: boolean): IPortalAssetsInitialize {
    return {
        type: PortalAssetsActions.PortalAssetsInitialize,
        supportMultipleLanguages,
    };
}

function SetActiveLanguageCode(languageCode: string): IPortalAssetsSetLanguageCode {
    return {
        type: PortalAssetsActions.PortalAssetsSetLanguageCode,
        languageCode,
    };
}

function GetLanguageAndLocale(): { languageCode: string, locale?: string } {
    const appState = store.getState();

    let languageCode = 'en';
    let locale;

    if (!!appState && !!appState.portalAssets) {
        languageCode = appState.portalAssets.languageCode;
        locale = appState.portalAssets.localeCode;
    }

    return {
        languageCode,
        locale,
    };
}

export async function RetrieveLoginText(): Promise<void> {
    store.dispatch({
        type: PortalAssetsActions.PortalAssetsRetrieveLoginText
    });

    try {
        const languageAndLocale = GetLanguageAndLocale();

        const result = await GetTenantAnonymousTextContent<PortalTextLogin>('loginPage', languageAndLocale.languageCode, languageAndLocale.locale);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveLoginTextCompleted,
            portalLoginText: result,
        });

    } catch (ex) {
        const errMsg = "Failure retrieving portal login text";

        LogException(errMsg, ex);
        console.error(errMsg);
        console.error(ex);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveLoginTextCompleted,
        });
    }
}

export async function RetrieveAccountingSystemsText(): Promise<void> {
    store.dispatch({
        type: PortalAssetsActions.PortalAssetsRetrieveAccountingSystemsText,
    });

    try {
        const languageAndLocale = GetLanguageAndLocale();

        const result = await GetTenantAccountingSystemsTextContent(languageAndLocale.languageCode, languageAndLocale.locale);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveAccountingSystemsTextCompleted,
            portalAccountingSystemsText: result,
        });

    } catch (ex) {
        const errMsg = "Failure retrieving portal accounting systems text";

        LogException(errMsg, ex);
        console.error(errMsg);
        console.error(ex);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveAccountingSystemsTextCompleted,
        });
    }
}

export async function RetrieveTimeoutText(): Promise<void> {
    store.dispatch({
        type: PortalAssetsActions.PortalAssetsRetrieveTimeoutText
    });

    try {
        const languageAndLocale = GetLanguageAndLocale();

        const result = await GetTenantAnonymousTextContent<PortalTextTimeoutMessaging>('timeoutMessaging', languageAndLocale.languageCode, languageAndLocale.locale);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveTimeoutTextCompleted,
            portalTimeoutText: result,
        });

    } catch (ex) {
        const errMsg = "Failure retrieving portal timeout text";

        LogException(errMsg, ex);
        console.error(errMsg);
        console.error(ex);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveTimeoutTextCompleted,
        });
    }
}

export async function RetrieveBorrowerAnonymousText(): Promise<void> {
    store.dispatch({
        type: PortalAssetsActions.PortalAssetsRetrieveBorrowerAnonymousText
    });

    try {
        const languageAndLocale = GetLanguageAndLocale();

        const result = await GetTenantAnonymousTextContent<PortalTextBorrowerAnonymousSubmission>('borrowerAnonymousSubmission', languageAndLocale.languageCode, languageAndLocale.locale);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveBorrowerAnonymousTextCompleted,
            portalBorrowerAnonymousText: result,
        });

    } catch (ex) {
        const errMsg = "Failure retrieving portal borrower anonymous text";

        LogException(errMsg, ex);
        console.error(errMsg);
        console.error(ex);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveBorrowerAnonymousTextCompleted,
        });
    }
}

function PruneCollectedSections(sectionIds: PortalTextSectionIds[]): PortalTextSectionIds[] {
    const appState = store.getState();
    if (!(appState && appState.portalAssets)) {
        return sectionIds;
    }
    const result: PortalTextSectionIds[] = [];

    sectionIds.forEach(sectionId => {
        const existing = appState.portalAssets.textSections.get(sectionId);
        const indexInFlight = appState.portalAssets.textSectionsInFlight.findIndex(sectionIdInFlight => sectionIdInFlight === sectionId);

        // If we don't already have the content and it's not in flight then we'll go get it.

        if ((!existing) && (indexInFlight === -1)) {
            result.push(sectionId);
        }
    });
    return result;
}

export async function RetrieveText(sectionIds: PortalTextSectionIds[], retrieveCollectedSections?: boolean): Promise<void> {
    let sectionIdsToUse = sectionIds.slice();

    try {
        const languageAndLocale = GetLanguageAndLocale();

        // If the option to retrieveCollectionSections is true, that means we should go get everything 
        // regardless of whether we have it or not already.  Otherwise, we should prune out sections
        // that we already have.  This includes sections that are in flight as well.
        if (!retrieveCollectedSections) {
            sectionIdsToUse = PruneCollectedSections(sectionIdsToUse);
            if (sectionIdsToUse.length <= 0) {
                return;
            }
        }

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveText,
            sectionIds: sectionIdsToUse,
        });

        const result = await GetTenantTextContent(
            sectionIdsToUse,
            languageAndLocale.languageCode,
            languageAndLocale.locale
        );

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveTextCompleted,
            portalTextResponse: result,
            sectionIds: sectionIdsToUse,
        });

        const iCommonImport = sectionIdsToUse.findIndex(id => id === 'commonImport');
        // If we got common import text there is some text that would have been set with the initial cookie
        // that needs to be fixed up
        if ((iCommonImport !== -1) && (!!result.content.commonImport)) {
            store.dispatch({
                type: AppSettingsActions.SetAccountingGroupText,
                commonImportText: result.content.commonImport,
            });
        }

    } catch (ex) {
        const errMsg = "Failure retrieving portal text";

        LogException(errMsg, ex);
        console.error(errMsg);
        console.error(ex);

        store.dispatch({
            type: PortalAssetsActions.PortalAssetsRetrieveTextCompleted,
            sectionIds: sectionIdsToUse,
        });
    }
}

export const PortalAssetsActionCreators = {
    PortalAssetsInitialize,
    SetActiveLanguageCode,
};

